import { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { dashboardApi, usersApi, api, PeriodoNecesidad } from '../../services/api'
import { Grid, Spinner, LoadingContainer } from '../../components/shared/StyledComponents'
import { AvailabilitySection } from './components/AvailabilitySection'
import { HiringNeedsSection } from './components/HiringNeedsSection'
import { PipelineSection } from './components/PipelineSection'
import { PersonsPanel } from './components/PersonsPanel'
import { OpportunitiesPanel, Opportunity } from './components/OpportunitiesPanel'
import { HiringNeedDetailPanel } from './components/HiringNeedDetailPanel'
import { BillingAlertsSection } from './components/BillingAlertsSection'
import { TeamBillingSection } from './components/TeamBillingSection'
import TeamLoadChart from './components/TeamLoadChart'
import { blockingEnd } from './availabilityRules'
import { useAuth } from '../../contexts/AuthContext'
import { SimilarClientsBox, type SimilarClientGroup } from './components/SimilarClientsBox'
import { isAdminRole, openOpportunity } from '../../utils/openOpportunity'

const PageTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.xl};
`

const PageSubtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const ExtendedStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const StatCard = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

interface StatValueColoredProps {
    positive?: boolean;
    negative?: boolean;
}

const StatValueColored = styled.div<StatValueColoredProps>`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
  color: ${({ positive, negative, theme }) =>
        positive ? '#2E7D32' :
            negative ? '#C62828' :
                theme.colors.primary};
`

const KPICard = styled.div<{ variant?: 'success' | 'danger' | 'warning' | 'info' }>`
  background: ${({ variant }) =>
        variant === 'success' ? 'linear-gradient(135deg, #E8F5E9, #fff)' :
            variant === 'danger' ? 'linear-gradient(135deg, #FFEBEE, #fff)' :
                variant === 'warning' ? 'linear-gradient(135deg, #FFF8E1, #fff)' :
                    variant === 'info' ? 'linear-gradient(135deg, #E3F2FD, #fff)' :
                        'white'};
  border: 1px solid ${({ variant }) =>
        variant === 'success' ? '#81C784' :
            variant === 'danger' ? '#EF9A9A' :
                variant === 'warning' ? '#FFE082' :
                    variant === 'info' ? '#90CAF9' :
                        '#e0e0e0'};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`

const KPIValue = styled.div<{ variant?: 'success' | 'danger' | 'warning' | 'info' }>`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
  color: ${({ variant }) =>
        variant === 'success' ? '#2E7D32' :
            variant === 'danger' ? '#C62828' :
                variant === 'warning' ? '#F57C00' :
                    variant === 'info' ? '#1565C0' :
                        '#333'};
`

const KPILabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.xl} 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
`

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const BreakdownCard = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
`

const BreakdownTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const BreakdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  &:last-child {
    border-bottom: none;
  }
`

const BreakdownCount = styled.span`
  background: ${({ theme }) => theme.colors.secondary};
  padding: 0.15rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

// New styled components for availability timeline and over-assigned
const AvailabilityTimelineCard = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const TimelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

interface TimelineCardProps {
    gradient: string
}

const TimelineCard = styled.div<TimelineCardProps>`
  background: ${({ gradient }) => gradient};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  color: white;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`

const TimelineValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
`

const TimelineLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const TimelineSubLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.85;
`

const OverAssignedSection = styled.div`
  background: linear-gradient(135deg, #ffebee, #fff);
  border: 2px solid #ef5350;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const OverAssignedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const OverAssignedTitle = styled.h3`
  margin: 0;
  color: #c62828;
  font-size: ${({ theme }) => theme.fontSizes.md};
  display: flex;
  align-items: center;
  gap: 8px;
`

const OverAssignedCount = styled.span`
  background: #c62828;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 700;
`

const OverAssignedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const OverAssignedPerson = styled.div`
  background: white;
  border: 1px solid #ef9a9a;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PersonName = styled.span`
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const PersonLoad = styled.span<{ load: number }>`
  font-weight: 700;
  color: ${({ load }) => load > 120 ? '#c62828' : '#ef5350'};
  font-size: ${({ theme }) => theme.fontSizes.md};
`

const formatAmount = (amount: number): string => {
    if (!amount) return '0€'
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M€`
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K€`
    return `${amount}€`
}

// The modal and the SharePoint import write oferta.venta; oferta.amount is the
// deprecated legacy field, kept as a fallback for old rows.
const offerAmount = (opp: any): number | undefined =>
    opp.oferta?.venta ?? opp.oferta?.amount ?? opp.oferta_venta ?? opp.oferta_amount ?? undefined

export const EstadisticasPage: React.FC = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const isAdmin = isAdminRole(user?.role)
    const [stats, setStats] = useState<any>(null)
    const [staleOffers, setStaleOffers] = useState<any[]>([])
    const [kpisByResponsable, setKpisByResponsable] = useState<any[]>([])
    const [kpisByCliente, setKpisByCliente] = useState<any[]>([])
    const [usersData, setUsersData] = useState<any[]>([])
    const [opportunitiesData, setOpportunitiesData] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [panelOpen, setPanelOpen] = useState(false)
    const [panelTitle, setPanelTitle] = useState('')
    const [panelPersons, setPanelPersons] = useState<any[]>([])
    const [panelDescription, setPanelDescription] = useState('')
    const [oppPanelOpen, setOppPanelOpen] = useState(false)
    const [oppPanelTitle, setOppPanelTitle] = useState('')
    const [oppPanelOpportunities, setOppPanelOpportunities] = useState<Opportunity[]>([])
    const [oppPanelDescription, setOppPanelDescription] = useState('')
    const [hiringNeedPanelOpen, setHiringNeedPanelOpen] = useState(false)
    const [hiringNeedPanelTitle, setHiringNeedPanelTitle] = useState('')
    const [selectedHiringPeriodo, setSelectedHiringPeriodo] = useState<PeriodoNecesidad | null>(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const [similarClients, setSimilarClients] = useState<SimilarClientGroup[]>([])

    // KPI table sorting, filtering and modal
    const [respSortCol, setRespSortCol] = useState<string | null>(null)
    const [respSortDir, setRespSortDir] = useState<'asc' | 'desc'>('desc')
    const [respFilter, setRespFilter] = useState('')
    const [clienteSortCol, setClienteSortCol] = useState<string | null>(null)
    const [clienteSortDir, setClienteSortDir] = useState<'asc' | 'desc'>('desc')
    const [showKpiModal, setShowKpiModal] = useState<'comercial' | 'cliente' | 'sector' | 'tecnologia' | null>(null)
    const [clienteFilter, setClienteFilter] = useState('')
    const [breakdownFilter, setBreakdownFilter] = useState('')
    // Rich KPI data per Sector / Tecnología — same schema as kpisByCliente.
    const [kpisBySector, setKpisBySector] = useState<any[]>([])
    const [kpisByTecnologia, setKpisByTecnologia] = useState<any[]>([])
    const [sectorSortCol, setSectorSortCol] = useState<string | null>('total')
    const [sectorSortDir, setSectorSortDir] = useState<'asc' | 'desc'>('desc')
    const [tecnologiaSortCol, setTecnologiaSortCol] = useState<string | null>('total')
    const [tecnologiaSortDir, setTecnologiaSortDir] = useState<'asc' | 'desc'>('desc')

    // Force refresh on every mount (navigation to this page)
    useEffect(() => {
        setRefreshKey(prev => prev + 1)
    }, [])

    useEffect(() => {
        loadData()
        loadSimilarClients()
    }, [refreshKey])

    const loadSimilarClients = useCallback(async () => {
        try {
            const res = await api.opportunities.getSimilarClients()
            setSimilarClients(res.data || [])
        } catch {
            // Ignore - non-critical
        }
    }, [])

    const loadData = async () => {
        setIsLoading(true)
        try {
            const [statsRes, matrixRes, opportunitiesRes, staleRes] = await Promise.all([
                dashboardApi.getStats(),
                usersApi.getMatrix(),  // Use matrix endpoint with pre-calculated capacity
                api.opportunities.getAll(),
                dashboardApi.getStaleOffers().catch(() => ({ data: [] })),
            ])
            setStats(statsRes.data)
            setStaleOffers(staleRes.data || [])

            // Load KPIs by responsable, cliente, sector, tecnología
            dashboardApi.getStatsByResponsable()
                .then(res => setKpisByResponsable(res.data || []))
                .catch(() => setKpisByResponsable([]))
            dashboardApi.getStatsByCliente()
                .then(res => setKpisByCliente(res.data || []))
                .catch(() => setKpisByCliente([]))
            dashboardApi.getStatsBySector()
                .then(res => setKpisBySector(res.data || []))
                .catch(() => setKpisBySector([]))
            dashboardApi.getStatsByTecnologia()
                .then(res => setKpisByTecnologia(res.data || []))
                .catch(() => setKpisByTecnologia([]))

            // Handle users from matrix - already has total_capacity calculated by backend
            // with the shared load rules (raw capacity_pct of the rows that count today)
            // Exclude admin users and interns from availability/hiring calculations
            const usersArr = Array.isArray(matrixRes.data?.users) ? matrixRes.data.users : []
            const filteredUsers = usersArr.filter((u: any) => {
                const username = (u.username || '').toLowerCase()
                const userType = (u.user_type || 'regular').toLowerCase()
                return username !== 'admin' && userType !== 'intern'
            })

            // Map users with their assignments
            const oppsArr = Array.isArray(opportunitiesRes.data) ? opportunitiesRes.data : []
            setOpportunitiesData(oppsArr)

            // Use backend-calculated total_capacity (same load as Timeline and the profile)
            const usersWithCapacity = filteredUsers.map((user: any) => ({
                ...user,
                total_capacity: Math.round((user.total_capacity || 0) * 10) / 10,
                assignments: user.projects || user.opportunities || [],
            }))

            setUsersData(usersWithCapacity)
        } catch (error) {
            console.error('Error loading stats:', error)
            // Try fallback to regular users endpoint
            try {
                const fallbackRes = await usersApi.getAll()
                const fallbackUsers = Array.isArray(fallbackRes.data) ? fallbackRes.data : []
                setUsersData(fallbackUsers)
            } catch (e) {
                console.error('Fallback also failed:', e)
            }
        }
        setIsLoading(false)
    }

    const calculateAvailability = () => {
        let sinCarga = 0, bajaCarga = 0, mediaCarga = 0, altaCarga = 0

        usersData.forEach((u) => {
            const cap = u.total_capacity || 0
            if (cap === 0) sinCarga++
            else if (cap <= 50) bajaCarga++
            else if (cap <= 80) mediaCarga++
            else altaCarga++
        })

        return { sinCarga, bajaCarga, mediaCarga, altaCarga }
    }

    // Calculate availability by timeframe from today's load (total_capacity, raw and
    // under the shared load rules) and from when the blocking assignments end.
    const calculateAvailabilityTimeline = () => {
        const now = new Date()
        const oneMonth = new Date(now); oneMonth.setMonth(oneMonth.getMonth() + 1)
        const threeMonths = new Date(now); threeMonths.setMonth(threeMonths.getMonth() + 3)
        const sixMonths = new Date(now); sixMonths.setMonth(sixMonths.getMonth() + 6)

        const result = {
            immediate: [] as any[], // Sin carga ahora (or under 30%)
            oneMonth: [] as any[],  // Disponibles en 1 mes
            shortTerm: [] as any[], // Corto plazo (1-3 meses)
            mediumTerm: [] as any[], // Medio plazo (3-6 meses)
            longTerm: [] as any[],  // Largo plazo (+6 meses)
        }

        usersData.forEach((user) => {
            const effectiveCap = user.total_capacity || 0

            // Under 30% load, the person can take new work now
            if (effectiveCap < 30) {
                result.immediate.push(user)
                return
            }

            const assignments = user.assignments || []
            if (assignments.length === 0) {
                result.immediate.push(user)
                return
            }

            // Latest end of the counting projects and alta/media offers; open-ended
            // (e.g. T&M with no end) means long term. See availabilityRules.ts.
            const { blocking, end } = blockingEnd(assignments)

            // Only low-probability offers: consider available soon
            if (!blocking) {
                result.oneMonth.push(user)
                return
            }

            if (end === null) {
                result.longTerm.push(user)
            } else if (end.getTime() <= oneMonth.getTime()) {
                result.oneMonth.push(user)
            } else if (end.getTime() <= threeMonths.getTime()) {
                result.shortTerm.push(user)
            } else if (end.getTime() <= sixMonths.getTime()) {
                result.mediumTerm.push(user)
            } else {
                result.longTerm.push(user)
            }
        })

        return result
    }

    // Get over-assigned users (>100%)
    const overAssignedUsers = useMemo(() => {
        return usersData
            .filter((u) => (u.total_capacity || 0) > 100)
            .sort((a, b) => (b.total_capacity || 0) - (a.total_capacity || 0))
    }, [usersData])

    // Filtered and sorted KPIs by Responsable
    const filteredSortedResponsable = useMemo(() => {
        let data = kpisByResponsable
        if (respFilter) {
            data = data.filter((r: any) => r.responsable?.toLowerCase().includes(respFilter.toLowerCase()))
        }
        if (respSortCol) {
            data = [...data].sort((a: any, b: any) => {
                const aVal = a[respSortCol] ?? 0
                const bVal = b[respSortCol] ?? 0
                if (respSortCol === 'responsable') {
                    return respSortDir === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal))
                }
                return respSortDir === 'asc' ? aVal - bVal : bVal - aVal
            })
        }
        return data
    }, [kpisByResponsable, respFilter, respSortCol, respSortDir])

    // Filtered and sorted KPIs by Cliente
    const filteredSortedCliente = useMemo(() => {
        let data = kpisByCliente
        if (clienteFilter) {
            data = data.filter((c: any) => c.cliente?.toLowerCase().includes(clienteFilter.toLowerCase()))
        }
        if (clienteSortCol) {
            data = [...data].sort((a: any, b: any) => {
                const aVal = a[clienteSortCol] ?? 0
                const bVal = b[clienteSortCol] ?? 0
                if (clienteSortCol === 'cliente') {
                    return clienteSortDir === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal))
                }
                return clienteSortDir === 'asc' ? aVal - bVal : bVal - aVal
            })
        }
        return data
    }, [kpisByCliente, clienteFilter, clienteSortCol, clienteSortDir])

    const handleRespSort = (col: string) => {
        if (respSortCol === col) {
            setRespSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setRespSortCol(col)
            setRespSortDir('desc')
        }
    }

    const handleClienteSort = (col: string) => {
        if (clienteSortCol === col) {
            setClienteSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setClienteSortCol(col)
            setClienteSortDir('desc')
        }
    }

    const handleSectorSort = (col: string) => {
        if (sectorSortCol === col) {
            setSectorSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSectorSortCol(col)
            setSectorSortDir('desc')
        }
    }

    const handleTecnologiaSort = (col: string) => {
        if (tecnologiaSortCol === col) {
            setTecnologiaSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setTecnologiaSortCol(col)
            setTecnologiaSortDir('desc')
        }
    }

    // Filtered + sorted KPIs by Sector / Tecnología (share breakdownFilter).
    const filteredSortedSector = useMemo(() => {
        let data = kpisBySector
        if (breakdownFilter) {
            data = data.filter((s: any) => s.sector?.toLowerCase().includes(breakdownFilter.toLowerCase()))
        }
        if (sectorSortCol) {
            data = [...data].sort((a: any, b: any) => {
                const aVal = a[sectorSortCol] ?? 0
                const bVal = b[sectorSortCol] ?? 0
                if (sectorSortCol === 'sector') {
                    return sectorSortDir === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal))
                }
                return sectorSortDir === 'asc' ? aVal - bVal : bVal - aVal
            })
        }
        return data
    }, [kpisBySector, breakdownFilter, sectorSortCol, sectorSortDir])

    const filteredSortedTecnologia = useMemo(() => {
        let data = kpisByTecnologia
        if (breakdownFilter) {
            data = data.filter((t: any) => t.tecnologia?.toLowerCase().includes(breakdownFilter.toLowerCase()))
        }
        if (tecnologiaSortCol) {
            data = [...data].sort((a: any, b: any) => {
                const aVal = a[tecnologiaSortCol] ?? 0
                const bVal = b[tecnologiaSortCol] ?? 0
                if (tecnologiaSortCol === 'tecnologia') {
                    return tecnologiaSortDir === 'asc'
                        ? String(aVal).localeCompare(String(bVal))
                        : String(bVal).localeCompare(String(aVal))
                }
                return tecnologiaSortDir === 'asc' ? aVal - bVal : bVal - aVal
            })
        }
        return data
    }, [kpisByTecnologia, breakdownFilter, tecnologiaSortCol, tecnologiaSortDir])

    const sortIndicator = (col: string, activeCol: string | null, dir: 'asc' | 'desc') => {
        if (col !== activeCol) return ' ↕'
        return dir === 'asc' ? ' ▲' : ' ▼'
    }

    const calculateHiringNeeds = () => {
        // Calculate hiring needs based on team availability + time horizons
        // NOTE: This is a FALLBACK calculation. The backend API /estadisticas/necesidad-contratacion
        // provides more accurate calculations including probability weighting per offer.
        // This fallback uses total_capacity (today's raw load under the shared load rules).
        const now = new Date()
        const oneMonthDate = new Date(now); oneMonthDate.setMonth(oneMonthDate.getMonth() + 1)
        const fourMonthsDate = new Date(now); fourMonthsDate.setMonth(fourMonthsDate.getMonth() + 4)
        const sixMonthsDate = new Date(now); sixMonthsDate.setMonth(sixMonthsDate.getMonth() + 6)

        // Pre-compute timestamps to avoid closure issues
        const oneMonthTime = oneMonthDate.getTime()
        const fourMonthsTime = fourMonthsDate.getTime()
        const sixMonthsTime = sixMonthsDate.getTime()

        const total = usersData.length || 1
        let sumCap = 0
        let overloadedNow = 0      // >100% effective capacity now
        let highLoadNow = 0        // >80% effective capacity now
        let availableNow = 0       // <30% effective capacity now (available for assignment)
        let availableInOneMonth = 0
        let availableInFourMonths = 0
        let availableInSixMonths = 0

        usersData.forEach((u) => {
            const cap = u.total_capacity || 0
            sumCap += cap

            // Count current load levels
            if (cap < 30) availableNow++  // Consider <30% as effectively available
            else if (cap > 100) overloadedNow++
            else if (cap > 80) highLoadNow++

            // When do the counting projects and alta/media offers end? Low-probability
            // offers don't block hiring decisions; open-ended ones (T&M) block beyond
            // the 6-month horizon. See availabilityRules.ts.
            const { blocking, end } = blockingEnd(u.assignments || [])

            if (blocking && end) {
                const endTime = end.getTime()
                if (endTime <= oneMonthTime) availableInOneMonth++
                else if (endTime <= fourMonthsTime) availableInFourMonths++
                else if (endTime <= sixMonthsTime) availableInSixMonths++
            } else if (!blocking && cap >= 30) {
                // Load only from low-probability offers = effectively available soon
                availableInOneMonth++
            }
        })

        const avgCap = sumCap / total

        // Total people available now or in short term (1 month)
        const shortTermAvailability = availableNow + availableInOneMonth

        // Hiring needs calculation:
        // - Inminente: Only if overloaded AND no one available now or in short term
        // - Corto (1 mes): Overloaded/high load but have some availability in short term
        // - Medio (4 meses): Based on avg capacity and availability in 4 months
        // - Largo (+6 meses): Based on sustained high average load

        // If we have people available now or soon, the need is short-term, not immediate
        const inmediateNeed = shortTermAvailability > 0 ? 0 : overloadedNow
        const shortTermNeed = shortTermAvailability > 0 ? Math.max(0, overloadedNow + highLoadNow - shortTermAvailability) : Math.max(0, highLoadNow)
        const mediumTermNeed = avgCap > 70 && (availableInFourMonths + shortTermAvailability) < 2 ? Math.ceil((avgCap - 70) / 15) : 0
        const longTermNeed = avgCap > 60 && (availableInSixMonths + availableInFourMonths + shortTermAvailability) < 3 ? 1 : 0

        return {
            inminente: inmediateNeed,
            corto: shortTermNeed,
            medio: mediumTermNeed,
            largo: longTermNeed,
        }
    }

    const showPersons = (type: string) => {
        const titles: Record<string, string> = {
            sin_carga: 'Personas sin cargabilidad',
            baja_carga: 'Personas con baja carga (1-50%)',
            media_carga: 'Personas con carga media (51-80%)',
            alta_carga: 'Personas con alta carga (>80%)',
        }

        const filtered = usersData.filter((u) => {
            const cap = u.total_capacity || 0
            if (type === 'sin_carga') return cap === 0
            if (type === 'baja_carga') return cap > 0 && cap <= 50
            if (type === 'media_carga') return cap > 50 && cap <= 80
            if (type === 'alta_carga') return cap > 80
            return false
        })

        setPanelTitle(`${titles[type]} (${filtered.length})`)
        setPanelPersons(filtered)
        setPanelDescription('')
        setPanelOpen(true)
    }

    const showAvailabilityByTimeframe = (type: string) => {
        const timeline = calculateAvailabilityTimeline()
        const titles: Record<string, string> = {
            immediate: 'Disponibles inmediatamente',
            oneMonth: 'Disponibles en 1 mes',
            shortTerm: 'Disponibles corto plazo (1-3 meses)',
            mediumTerm: 'Disponibles medio plazo (3-6 meses)',
            longTerm: 'Disponibles largo plazo (+6 meses)',
        }

        const descriptions: Record<string, string> = {
            immediate: 'Personas con menos del 30% de carga hoy (sin asignaciones o con poca carga)',
            oneMonth: 'Personas cuyas asignaciones actuales finalizan en menos de 1 mes',
            shortTerm: 'Personas cuyas asignaciones actuales finalizan entre 1 y 3 meses',
            mediumTerm: 'Personas cuyas asignaciones actuales finalizan entre 3 y 6 meses',
            longTerm: 'Personas con asignaciones que continúan más de 6 meses o sin fecha de fin (p. ej. T&M)',
        }

        const filtered = timeline[type as keyof typeof timeline] || []

        setPanelTitle(`${titles[type]} (${filtered.length})`)
        setPanelPersons(filtered)
        setPanelDescription(descriptions[type])
        setPanelOpen(true)
    }

    const showHiringNeed = (type: string, periodo: PeriodoNecesidad | null) => {
        const titles: Record<string, string> = {
            inminente: 'Necesidad Inminente',
            corto: 'Necesidad Corto Plazo (1 mes)',
            medio: 'Necesidad Medio Plazo (4 meses)',
            largo: 'Necesidad Largo Plazo (+6 meses)',
        }

        // If periodo data is provided from HiringNeedsSection, show it in the panel
        if (periodo) {
            setHiringNeedPanelTitle(titles[type] || periodo.periodo_label)
            setSelectedHiringPeriodo(periodo)
            setHiringNeedPanelOpen(true)
            return
        }

        // Fallback: filter local users by capacity (for old card-based filtering)

        const descriptions: Record<string, string> = {
            inminente: 'Personas con sobrecarga crítica (>120%)',
            corto: 'Personas cerca del límite de capacidad (100-120%)',
            medio: 'Previsión basada en carga promedio alta del equipo',
            largo: 'Proyección a largo plazo basada en tendencias actuales',
        }

        let filtered: any[] = []
        if (type === 'inminente') {
            filtered = usersData.filter((u) => (u.total_capacity || 0) > 120)
        } else if (type === 'corto') {
            filtered = usersData.filter((u) => {
                const cap = u.total_capacity || 0
                return cap > 100 && cap <= 120
            })
        } else if (type === 'medio') {
            filtered = usersData.filter((u) => {
                const cap = u.total_capacity || 0
                return cap > 80 && cap <= 100
            })
        } else if (type === 'largo') {
            filtered = usersData.filter((u) => {
                const cap = u.total_capacity || 0
                return cap > 60 && cap <= 80
            })
        }

        setPanelTitle(titles[type])
        setPanelPersons(filtered)
        setPanelDescription(descriptions[type])
        setPanelOpen(true)
    }

    // Status color mapping
    const getStatusColor = (status: string): string => {
        const colors: Record<string, string> = {
            ganada: '#2E7D32',
            perdida: '#C62828',
            cancelada: '#C62828',
            en_curso: '#1565C0',
            en_negociacion: '#FF9800',
            presentada: '#7B1FA2',
            en_preparacion: '#0288D1',
            en_revision: '#0288D1',
            borrador: '#757575',
            activo: '#1565C0',
            pendiente: '#FF9800',
            prospecto: '#7B1FA2',
            planificacion: '#0288D1',
            finalizado: '#2E7D32',
            completado: '#2E7D32',
            cerrado: '#757575',
            no_iniciado: '#9E9E9E',
            en_espera: '#FF9800',
            incompleta: '#FF5722',
        }
        return colors[status?.toLowerCase()] || '#9E9E9E'
    }

    const formatStatusLabel = (status: string): string => {
        const labels: Record<string, string> = {
            ganada: 'Ganada',
            perdida: 'Perdida',
            cancelada: 'Cancelada',
            en_curso: 'En Curso',
            en_negociacion: 'En Negociación',
            presentada: 'Presentada',
            en_preparacion: 'En Preparación',
            en_revision: 'En Revisión',
            borrador: 'Borrador',
            activo: 'Activo',
            pendiente: 'Pendiente',
            prospecto: 'Prospecto',
            planificacion: 'Planificación',
            finalizado: 'Finalizado',
            completado: 'Completado',
            cerrado: 'Cerrado',
            no_iniciado: 'No Iniciado',
            en_espera: 'En Espera',
            incompleta: 'Incompleta',
        }
        return labels[status?.toLowerCase()] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Sin estado'
    }

    const isOfertaIncomplete = (opp: any) => {
        const name = opp.general?.name || opp.name || ''
        const gestor = opp.general?.gestor || opp.gestor || ''
        const sector = opp.general?.sector || opp.sector || ''
        const techs = opp.general?.tecnologias || opp.tecnologias || []
        const equipo = opp.oferta?.equipo || []
        return !name.trim() || !gestor.trim() || !sector.trim() || techs.length === 0 || equipo.length === 0
    }

    const isProyectoIncomplete = (opp: any) => {
        const codigo = opp.proyecto?.codigo || ''
        const fechaInicio = opp.proyecto?.fecha_inicio || ''
        const fechaFin = opp.proyecto?.fecha_fin || ''
        const timeMaterial = opp.general?.time_material || false
        const equipo = opp.proyecto?.equipo || []
        return !codigo.trim() || !fechaInicio || (!timeMaterial && !fechaFin) || equipo.length === 0
    }

    const showIncompleteOfertas = () => {
        const filtered = opportunitiesData.filter((opp: any) => {
            const estado = opp.oferta?.estado || opp.oferta_estado || ''
            return estado && !['perdida', 'cancelada'].includes(estado.toLowerCase()) && isOfertaIncomplete(opp)
        })
        const mapped: Opportunity[] = filtered.map((opp: any) => ({
            id: String(opp.id),
            name: opp.general?.name || opp.name || 'Sin nombre',
            status: formatStatusLabel(opp.oferta?.estado || opp.oferta_estado || ''),
            statusColor: '#FF9800',
            sector: opp.general?.sector || opp.sector,
            gestor: opp.general?.gestor || opp.gestor,
            amount: offerAmount(opp),
            probability: opp.oferta?.probabilidad || opp.oferta_probabilidad,
        }))
        setOppPanelTitle(`Ofertas Incompletas (${mapped.length})`)
        setOppPanelOpportunities(mapped)
        setOppPanelDescription('Oportunidades en fase oferta con campos pendientes')
        setOppPanelOpen(true)
    }

    const showIncompleteProyectos = () => {
        const filtered = opportunitiesData.filter((opp: any) => {
            const estado = opp.proyecto?.estado || opp.proyecto_estado || ''
            return estado && estado.trim() !== '' && isProyectoIncomplete(opp)
        })
        const mapped: Opportunity[] = filtered.map((opp: any) => ({
            id: String(opp.id),
            name: opp.general?.name || opp.name || 'Sin nombre',
            status: formatStatusLabel(opp.proyecto?.estado || opp.proyecto_estado || ''),
            statusColor: '#FF9800',
            sector: opp.general?.sector || opp.sector,
            gestor: opp.proyecto?.responsable || opp.proyecto_responsable,
            amount: offerAmount(opp),
        }))
        setOppPanelTitle(`Proyectos Incompletos (${mapped.length})`)
        setOppPanelOpportunities(mapped)
        setOppPanelDescription('Oportunidades en fase proyecto con campos pendientes')
        setOppPanelOpen(true)
    }

    const showOfertasByStage = (stageName: string) => {
        // Filter opportunities by oferta estado
        const filtered = opportunitiesData.filter((opp: any) => {
            const estado = opp.oferta?.estado || opp.oferta_estado || ''
            return formatStatusLabel(estado).toLowerCase() === stageName.toLowerCase()
        })

        const mappedOpportunities: Opportunity[] = filtered.map((opp: any) => ({
            id: String(opp.id),
            name: opp.general?.name || opp.name || 'Sin nombre',
            status: formatStatusLabel(opp.oferta?.estado || opp.oferta_estado || ''),
            statusColor: getStatusColor(opp.oferta?.estado || opp.oferta_estado || ''),
            sector: opp.general?.sector || opp.sector,
            gestor: opp.general?.gestor || opp.gestor,
            amount: offerAmount(opp),
            probability: opp.oferta?.probabilidad || opp.oferta_probabilidad,
        }))

        setOppPanelTitle(`${stageName} (${mappedOpportunities.length})`)
        setOppPanelOpportunities(mappedOpportunities)
        setOppPanelDescription(`Oportunidades en estado "${stageName}"`)
        setOppPanelOpen(true)
    }

    const showProyectosByStage = (stageName: string) => {
        // Filter opportunities by proyecto estado
        const filtered = opportunitiesData.filter((opp: any) => {
            const estado = opp.proyecto?.estado || opp.proyecto_estado || ''
            return formatStatusLabel(estado).toLowerCase() === stageName.toLowerCase()
        })

        const mappedOpportunities: Opportunity[] = filtered.map((opp: any) => ({
            id: String(opp.id),
            name: opp.general?.name || opp.name || 'Sin nombre',
            status: formatStatusLabel(opp.proyecto?.estado || opp.proyecto_estado || ''),
            statusColor: getStatusColor(opp.proyecto?.estado || opp.proyecto_estado || ''),
            sector: opp.general?.sector || opp.sector,
            gestor: opp.proyecto?.responsable || opp.proyecto_responsable,
            amount: offerAmount(opp),
        }))

        setOppPanelTitle(`${stageName} (${mappedOpportunities.length})`)
        setOppPanelOpportunities(mappedOpportunities)
        setOppPanelDescription(`Proyectos en estado "${stageName}"`)
        setOppPanelOpen(true)
    }

    if (isLoading) {
        return (
            <LoadingContainer>
                <Spinner />
            </LoadingContainer>
        )
    }

    const availability = calculateAvailability()
    const hiringNeeds = calculateHiringNeeds()
    const availabilityTimeline = calculateAvailabilityTimeline()

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                    <PageTitle>📊 Estadísticas del Departamento</PageTitle>
                    <PageSubtitle>Métricas, disponibilidad y necesidades de recursos</PageSubtitle>
                </div>
                <button
                    onClick={() => setRefreshKey(prev => prev + 1)}
                    disabled={isLoading}
                    title="Recargar KPIs (útil tras crear/unificar clientes)"
                    style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid #1976D2',
                        background: isLoading ? '#BBDEFB' : 'white',
                        color: '#1976D2',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isLoading ? '⏳ Cargando…' : '🔄 Actualizar'}
                </button>
            </div>

            <StatsGrid>
                <StatCard>
                    <StatValue>{stats?.summary?.total_opportunities || 0}</StatValue>
                    <StatLabel>Total Oportunidades</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{stats?.summary?.active_opportunities || 0}</StatValue>
                    <StatLabel>Activas</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{usersData.length}</StatValue>
                    <StatLabel>Equipo</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{usersData.filter(u => (u.total_capacity || 0) > 0).length}</StatValue>
                    <StatLabel>Asignados</StatLabel>
                </StatCard>
                <StatCard>
                    <StatValue>{formatAmount(stats?.summary?.total_importe || 0)}</StatValue>
                    <StatLabel>Importe Total</StatLabel>
                </StatCard>
            </StatsGrid>

            {/* Extended KPIs Section */}
            <SectionTitle>💰 KPIs Financieros</SectionTitle>
            <ExtendedStatsGrid>
                <KPICard variant="success">
                    <KPIValue variant="success">
                        {stats?.extended_stats?.ofertas?.tasa_ganadas?.toFixed(1) || 0}%
                    </KPIValue>
                    <KPILabel>Tasa Ofertas Ganadas</KPILabel>
                </KPICard>
                <KPICard variant="danger">
                    <KPIValue variant="danger">
                        {stats?.extended_stats?.ofertas?.tasa_perdidas?.toFixed(1) || 0}%
                    </KPIValue>
                    <KPILabel>Tasa Ofertas Perdidas</KPILabel>
                </KPICard>
                <KPICard variant="info">
                    <KPIValue variant="info">
                        {formatAmount(stats?.extended_stats?.financiero?.venta_total || 0)}
                    </KPIValue>
                    <KPILabel>Venta Total</KPILabel>
                </KPICard>
                <KPICard variant="warning">
                    <KPIValue variant="warning">
                        {formatAmount(stats?.extended_stats?.financiero?.coste_interno_total || 0)}
                    </KPIValue>
                    <KPILabel>Coste Interno Total</KPILabel>
                </KPICard>
            </ExtendedStatsGrid>

            <ExtendedStatsGrid>
                <KPICard variant={
                    (stats?.extended_stats?.financiero?.rentabilidad_total || 0) >= 0 ? 'success' : 'danger'
                }>
                    <KPIValue variant={
                        (stats?.extended_stats?.financiero?.rentabilidad_total || 0) >= 0 ? 'success' : 'danger'
                    }>
                        {formatAmount(stats?.extended_stats?.financiero?.rentabilidad_total || 0)}
                    </KPIValue>
                    <KPILabel>Rentabilidad (Venta - Coste)</KPILabel>
                </KPICard>
                <KPICard variant={
                    (stats?.extended_stats?.financiero?.margen_rentabilidad || 0) >= 20 ? 'success' :
                        (stats?.extended_stats?.financiero?.margen_rentabilidad || 0) >= 10 ? 'warning' : 'danger'
                }>
                    <KPIValue variant={
                        (stats?.extended_stats?.financiero?.margen_rentabilidad || 0) >= 20 ? 'success' :
                            (stats?.extended_stats?.financiero?.margen_rentabilidad || 0) >= 10 ? 'warning' : 'danger'
                    }>
                        {stats?.extended_stats?.financiero?.margen_rentabilidad?.toFixed(1) || 0}%
                    </KPIValue>
                    <KPILabel>Margen de Rentabilidad</KPILabel>
                </KPICard>
                <KPICard variant="info">
                    <KPIValue variant="info">
                        {stats?.extended_stats?.proyectos?.tasa_conversion?.toFixed(1) || 0}%
                    </KPIValue>
                    <KPILabel>Conversión Oferta → Proyecto</KPILabel>
                </KPICard>
                <KPICard variant="success">
                    <KPIValue variant="success">
                        {stats?.extended_stats?.ofertas?.ganadas || 0}
                    </KPIValue>
                    <KPILabel>Ofertas Ganadas</KPILabel>
                </KPICard>
            </ExtendedStatsGrid>

            {/* Over-assigned users section */}
            {overAssignedUsers.length > 0 && (
                <OverAssignedSection>
                    <OverAssignedHeader>
                        <OverAssignedTitle>
                            ⚠️ Personas sobre-asignadas (&gt;100%)
                        </OverAssignedTitle>
                        <OverAssignedCount>{overAssignedUsers.length}</OverAssignedCount>
                    </OverAssignedHeader>
                    <OverAssignedGrid>
                        {overAssignedUsers.map((user) => (
                            <OverAssignedPerson key={user.id}>
                                <PersonName>{user.display_name || user.username}</PersonName>
                                <PersonLoad load={user.total_capacity}>
                                    {user.total_capacity}%
                                </PersonLoad>
                            </OverAssignedPerson>
                        ))}
                    </OverAssignedGrid>
                </OverAssignedSection>
            )}

            {/* Availability Timeline */}
            <AvailabilityTimelineCard>
                <h3 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>📅 Cargabilidad por Horizonte Temporal</h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '0.8rem', color: '#666' }}>
                    Personas disponibles según cuándo finalizan sus asignaciones actuales
                </p>
                <TimelineGrid>
                    <TimelineCard
                        gradient="linear-gradient(135deg, #C62828, #E53935)"
                        onClick={() => showAvailabilityByTimeframe('immediate')}
                    >
                        <TimelineValue>{availabilityTimeline.immediate.length}</TimelineValue>
                        <TimelineLabel>Disponible ahora</TimelineLabel>
                        <TimelineSubLabel>Carga &lt; 30%</TimelineSubLabel>
                    </TimelineCard>
                    <TimelineCard
                        gradient="linear-gradient(135deg, #E65100, #F57C00)"
                        onClick={() => showAvailabilityByTimeframe('oneMonth')}
                    >
                        <TimelineValue>{availabilityTimeline.oneMonth.length}</TimelineValue>
                        <TimelineLabel>1 mes</TimelineLabel>
                        <TimelineSubLabel>Próximamente</TimelineSubLabel>
                    </TimelineCard>
                    <TimelineCard
                        gradient="linear-gradient(135deg, #F9A825, #FBC02D)"
                        onClick={() => showAvailabilityByTimeframe('shortTerm')}
                    >
                        <TimelineValue>{availabilityTimeline.shortTerm.length}</TimelineValue>
                        <TimelineLabel>Corto plazo</TimelineLabel>
                        <TimelineSubLabel>1-3 meses</TimelineSubLabel>
                    </TimelineCard>
                    <TimelineCard
                        gradient="linear-gradient(135deg, #558B2F, #7CB342)"
                        onClick={() => showAvailabilityByTimeframe('mediumTerm')}
                    >
                        <TimelineValue>{availabilityTimeline.mediumTerm.length}</TimelineValue>
                        <TimelineLabel>Medio plazo</TimelineLabel>
                        <TimelineSubLabel>3-6 meses</TimelineSubLabel>
                    </TimelineCard>
                    <TimelineCard
                        gradient="linear-gradient(135deg, #2E7D32, #43A047)"
                        onClick={() => showAvailabilityByTimeframe('longTerm')}
                    >
                        <TimelineValue>{availabilityTimeline.longTerm.length}</TimelineValue>
                        <TimelineLabel>Largo plazo</TimelineLabel>
                        <TimelineSubLabel>+6 meses</TimelineSubLabel>
                    </TimelineCard>
                </TimelineGrid>
            </AvailabilityTimelineCard>

            <TwoColumns>
                <AvailabilitySection availability={availability} onCardClick={showPersons} />
                <HiringNeedsSection hiringNeeds={hiringNeeds} onCardClick={showHiringNeed} />
            </TwoColumns>

            <TeamLoadChart />

            <SectionTitle>💼 Pipeline de Ofertas</SectionTitle>
            <PipelineSection
                stages={stats?.ofertas?.stages || []}
                incompleteCount={stats?.ofertas?.incomplete}
                onStageClick={showOfertasByStage}
                onIncompleteClick={showIncompleteOfertas}
            />

            <SectionTitle>🚀 Pipeline de Proyectos</SectionTitle>
            <PipelineSection
                stages={stats?.proyectos?.stages || []}
                incompleteCount={stats?.proyectos?.incomplete}
                onStageClick={showProyectosByStage}
                onIncompleteClick={showIncompleteProyectos}
            />

            {staleOffers.length > 0 && (
                <>
                    <SectionTitle>⏰ Ofertas Estancadas</SectionTitle>
                    <div style={{
                        background: 'linear-gradient(135deg, #FFF8E1, #fff)',
                        border: '1px solid #FFB74D',
                        borderRadius: '12px',
                        padding: '1rem',
                        marginBottom: '1.5rem',
                    }}>
                        <div style={{ fontSize: '0.85rem', color: '#E65100', marginBottom: '0.75rem', fontWeight: 600 }}>
                            {staleOffers.length} oferta{staleOffers.length > 1 ? 's' : ''} lleva{staleOffers.length > 1 ? 'n' : ''} demasiado tiempo sin avanzar
                        </div>
                        {staleOffers.map((offer: any) => (
                            <div
                                key={offer.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0.5rem 0.75rem',
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: '8px',
                                    marginBottom: '0.5rem',
                                    cursor: 'pointer',
                                }}
                                onClick={() => openOpportunity(navigate, offer.id, { isAdmin })}
                            >
                                <div>
                                    <span style={{ fontWeight: 500 }}>{offer.name}</span>
                                    <span style={{
                                        marginLeft: '0.5rem',
                                        fontSize: '0.75rem',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        background: offer.oferta_estado === 'presentada' ? '#E1BEE7' : '#FFE0B2',
                                        color: offer.oferta_estado === 'presentada' ? '#6A1B9A' : '#E65100',
                                    }}>
                                        {offer.oferta_estado === 'presentada' ? 'Presentada' : 'En negociación'}
                                    </span>
                                </div>
                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#E65100' }}>
                                    {offer.days_in_status} días
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <SectionTitle>📈 KPIs por Categoría</SectionTitle>
            <BreakdownGrid>
                <BreakdownCard>
                    <BreakdownTitle>🏢 Por Sector</BreakdownTitle>
                    {(() => {
                        const items = (stats?.breakdowns?.by_sector || [])
                            .filter((item: any) => item.name && item.name.trim())
                        if (items.length === 0) {
                            return <p style={{ color: '#999', textAlign: 'center' }}>Sin datos</p>
                        }
                        return (
                            <>
                                {items.slice(0, 20).map((item: any) => (
                                    <BreakdownItem key={item.name}>
                                        <span>{item.name.replace(/_/g, ' ')}</span>
                                        <BreakdownCount>{item.count}</BreakdownCount>
                                    </BreakdownItem>
                                ))}
                                <button
                                    onClick={() => { setBreakdownFilter(''); setShowKpiModal('sector') }}
                                    style={{ marginTop: '8px', background: 'none', border: 'none', color: '#1976D2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, padding: 0 }}
                                >
                                    Ver más ({items.length})
                                </button>
                            </>
                        )
                    })()}
                </BreakdownCard>
                <BreakdownCard>
                    <BreakdownTitle>🔧 Por Tecnología</BreakdownTitle>
                    {(() => {
                        const items = (stats?.breakdowns?.by_tecnologia || [])
                            .filter((item: any) => item.name && item.name.trim())
                        if (items.length === 0) {
                            return <p style={{ color: '#999', textAlign: 'center' }}>Sin datos</p>
                        }
                        return (
                            <>
                                {items.slice(0, 20).map((item: any) => (
                                    <BreakdownItem key={item.name}>
                                        <span>{item.name.replace(/_/g, ' ')}</span>
                                        <BreakdownCount>{item.count}</BreakdownCount>
                                    </BreakdownItem>
                                ))}
                                <button
                                    onClick={() => { setBreakdownFilter(''); setShowKpiModal('tecnologia') }}
                                    style={{ marginTop: '8px', background: 'none', border: 'none', color: '#1976D2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, padding: 0 }}
                                >
                                    Ver más ({items.length})
                                </button>
                            </>
                        )
                    })()}
                </BreakdownCard>
                <BreakdownCard>
                    <BreakdownTitle>👤 Por Comercial</BreakdownTitle>
                    {kpisByResponsable.length === 0 ? (
                        <p style={{ color: '#999', textAlign: 'center' }}>Sin datos</p>
                    ) : (
                        <>
                            {[...kpisByResponsable].sort((a, b) => (b.ganadas || 0) - (a.ganadas || 0)).slice(0, 20).map((r: any) => (
                                <BreakdownItem key={r.responsable}>
                                    <span>{r.responsable}</span>
                                    <BreakdownCount>{r.ganadas || 0}</BreakdownCount>
                                </BreakdownItem>
                            ))}
                            {kpisByResponsable.length > 20 && (
                                <button
                                    onClick={() => setShowKpiModal('comercial')}
                                    style={{ marginTop: '8px', background: 'none', border: 'none', color: '#1976D2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, padding: 0 }}
                                >
                                    Ver más ({kpisByResponsable.length})
                                </button>
                            )}
                        </>
                    )}
                </BreakdownCard>
                <BreakdownCard>
                    <BreakdownTitle>🏭 Por Cliente</BreakdownTitle>
                    <SimilarClientsBox
                        groups={similarClients}
                        isAdmin={isAdmin}
                        onChanged={() => {
                            loadData()
                            loadSimilarClients()
                        }}
                    />
                    {kpisByCliente.length === 0 ? (
                        <p style={{ color: '#999', textAlign: 'center' }}>Sin datos</p>
                    ) : (
                        <>
                            {[...kpisByCliente].sort((a, b) => (b.ganadas || 0) - (a.ganadas || 0)).slice(0, 20).map((c: any) => (
                                <BreakdownItem key={c.cliente}>
                                    <span>{c.cliente}</span>
                                    <BreakdownCount>{c.ganadas || 0}</BreakdownCount>
                                </BreakdownItem>
                            ))}
                            {kpisByCliente.length > 20 && (
                                <button
                                    onClick={() => setShowKpiModal('cliente')}
                                    style={{ marginTop: '8px', background: 'none', border: 'none', color: '#1976D2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500, padding: 0 }}
                                >
                                    Ver más ({kpisByCliente.length})
                                </button>
                            )}
                        </>
                    )}
                </BreakdownCard>
            </BreakdownGrid>

            {/* Billing Alerts Section */}
            <BillingAlertsSection />

            {/* Team Billing/Imputaciones Section */}
            <TeamBillingSection />

            <PersonsPanel
                isOpen={panelOpen}
                onClose={() => setPanelOpen(false)}
                title={panelTitle}
                description={panelDescription}
                persons={panelPersons.map(p => ({
                    id: p.id,
                    name: p.display_name || p.username,
                    role: p.job_role || p.department,
                    load: p.total_capacity,
                }))}
                showLoad={true}
            />

            <OpportunitiesPanel
                isOpen={oppPanelOpen}
                onClose={() => setOppPanelOpen(false)}
                title={oppPanelTitle}
                description={oppPanelDescription}
                opportunities={oppPanelOpportunities}
                // Open the clicked opportunity (the list used to open without it). The
                // page is admin-only; a non-admin would get plain, non-clickable rows.
                onOpportunityClick={isAdmin ? (opp) => {
                    setOppPanelOpen(false)
                    openOpportunity(navigate, opp.id, { isAdmin })
                } : undefined}
            />

            <HiringNeedDetailPanel
                isOpen={hiringNeedPanelOpen}
                title={hiringNeedPanelTitle}
                onClose={() => {
                    setHiringNeedPanelOpen(false)
                    setSelectedHiringPeriodo(null)
                }}
                periodo={selectedHiringPeriodo}
            />

            {/* KPI Detail Modal */}
            {showKpiModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowKpiModal(null) }}>
                    <div style={{ background: 'white', borderRadius: '16px', width: '95%', maxWidth: '1000px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #eee' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                                {showKpiModal === 'comercial' ? 'KPIs por Comercial'
                                    : showKpiModal === 'cliente' ? 'KPIs por Cliente'
                                    : showKpiModal === 'sector' ? 'KPIs por Sector'
                                    : 'KPIs por Tecnología'}
                            </h3>
                            <button onClick={() => setShowKpiModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999', padding: '0 4px' }}>&times;</button>
                        </div>
                        <div style={{ padding: '1rem 1.5rem' }}>
                            <input
                                type="text"
                                placeholder={showKpiModal === 'comercial' ? 'Filtrar por comercial...'
                                    : showKpiModal === 'cliente' ? 'Filtrar por cliente...'
                                    : showKpiModal === 'sector' ? 'Filtrar por sector...'
                                    : 'Filtrar por tecnología...'}
                                value={showKpiModal === 'comercial' ? respFilter
                                    : showKpiModal === 'cliente' ? clienteFilter
                                    : breakdownFilter}
                                onChange={(e) => {
                                    if (showKpiModal === 'comercial') setRespFilter(e.target.value)
                                    else if (showKpiModal === 'cliente') setClienteFilter(e.target.value)
                                    else setBreakdownFilter(e.target.value)
                                }}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e0e0e0', width: '280px', fontSize: '0.85rem' }}
                            />
                        </div>
                        <div style={{ overflow: 'auto', flex: 1, padding: '0 1.5rem 1.5rem' }}>
                            {(showKpiModal === 'sector' || showKpiModal === 'tecnologia') ? (
                                (() => {
                                    const isSector = showKpiModal === 'sector'
                                    const keyCol = isSector ? 'sector' : 'tecnologia'
                                    const rows = isSector ? filteredSortedSector : filteredSortedTecnologia
                                    const sortCol = isSector ? sectorSortCol : tecnologiaSortCol
                                    const sortDir = isSector ? sectorSortDir : tecnologiaSortDir
                                    const onSort = isSector ? handleSectorSort : handleTecnologiaSort
                                    const columns = [
                                        { key: keyCol, label: isSector ? 'Sector' : 'Tecnología', align: 'left' as const },
                                        { key: 'total', label: 'Total', align: 'center' as const },
                                        { key: 'ganadas', label: 'Ganadas', align: 'center' as const },
                                        { key: 'perdidas', label: 'Perdidas', align: 'center' as const },
                                        { key: 'tasa_ganadas', label: '% Ganadas', align: 'center' as const },
                                        { key: 'venta_total', label: 'Venta', align: 'center' as const },
                                        { key: 'rentabilidad', label: 'Rentabilidad', align: 'center' as const },
                                        { key: 'margen_rentabilidad', label: 'Margen', align: 'center' as const },
                                    ]
                                    return (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                                                    {columns.map(col => (
                                                        <th key={col.key}
                                                            style={{ padding: '0.75rem', textAlign: col.align, cursor: 'pointer', userSelect: 'none' }}
                                                            onClick={() => onSort(col.key)}
                                                        >
                                                            {col.label}{sortIndicator(col.key, sortCol, sortDir)}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((r: any, i: number) => (
                                                    <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                                                        <td style={{ padding: '0.75rem', fontWeight: 500 }}>{String(r[keyCol] ?? '').replace(/_/g, ' ')}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{r.total}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', color: '#2E7D32' }}>{r.ganadas}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', color: '#C62828' }}>{r.perdidas}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: r.tasa_ganadas >= 50 ? '#2E7D32' : '#E65100' }}>{r.tasa_ganadas}%</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{formatAmount(r.venta_total)}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', color: r.rentabilidad >= 0 ? '#2E7D32' : '#C62828' }}>{formatAmount(r.rentabilidad)}</td>
                                                        <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: r.margen_rentabilidad >= 20 ? '#2E7D32' : r.margen_rentabilidad >= 10 ? '#E65100' : '#C62828' }}>{r.margen_rentabilidad}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )
                                })()
                            ) : showKpiModal === 'comercial' ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                                            {[
                                                { key: 'responsable', label: 'Comercial', align: 'left' as const },
                                                { key: 'total', label: 'Total', align: 'center' as const },
                                                { key: 'ganadas', label: 'Ganadas', align: 'center' as const },
                                                { key: 'perdidas', label: 'Perdidas', align: 'center' as const },
                                                { key: 'canceladas', label: 'Canceladas', align: 'center' as const },
                                                { key: 'tasa_ganadas', label: '% Ganadas', align: 'center' as const },
                                                { key: 'venta_total', label: 'Venta', align: 'center' as const },
                                                { key: 'rentabilidad', label: 'Rentabilidad', align: 'center' as const },
                                                { key: 'margen_rentabilidad', label: 'Margen', align: 'center' as const },
                                            ].map(col => (
                                                <th key={col.key}
                                                    style={{ padding: '0.75rem', textAlign: col.align, cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleRespSort(col.key)}
                                                >
                                                    {col.label}{sortIndicator(col.key, respSortCol, respSortDir)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSortedResponsable.map((r: any, i: number) => (
                                            <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                                                <td style={{ padding: '0.75rem', fontWeight: 500 }}>{r.responsable}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{r.total}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: '#2E7D32' }}>{r.ganadas}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: '#C62828' }}>{r.perdidas}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: '#546E7A' }}>{r.canceladas || 0}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: r.tasa_ganadas >= 50 ? '#2E7D32' : '#E65100' }}>{r.tasa_ganadas}%</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{formatAmount(r.venta_total)}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: r.rentabilidad >= 0 ? '#2E7D32' : '#C62828' }}>{formatAmount(r.rentabilidad)}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: r.margen_rentabilidad >= 20 ? '#2E7D32' : r.margen_rentabilidad >= 10 ? '#E65100' : '#C62828' }}>{r.margen_rentabilidad}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                                            {[
                                                { key: 'cliente', label: 'Cliente', align: 'left' as const },
                                                { key: 'total', label: 'Total', align: 'center' as const },
                                                { key: 'ganadas', label: 'Ganadas', align: 'center' as const },
                                                { key: 'perdidas', label: 'Perdidas', align: 'center' as const },
                                                { key: 'tasa_ganadas', label: '% Ganadas', align: 'center' as const },
                                                { key: 'venta_total', label: 'Venta', align: 'center' as const },
                                                { key: 'rentabilidad', label: 'Rentabilidad', align: 'center' as const },
                                                { key: 'margen_rentabilidad', label: 'Margen', align: 'center' as const },
                                            ].map(col => (
                                                <th key={col.key}
                                                    style={{ padding: '0.75rem', textAlign: col.align, cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => handleClienteSort(col.key)}
                                                >
                                                    {col.label}{sortIndicator(col.key, clienteSortCol, clienteSortDir)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSortedCliente.map((c: any, i: number) => (
                                            <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                                                <td style={{ padding: '0.75rem', fontWeight: 500 }}>{c.cliente}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{c.total}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: '#2E7D32' }}>{c.ganadas}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: '#C62828' }}>{c.perdidas}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: c.tasa_ganadas >= 50 ? '#2E7D32' : '#E65100' }}>{c.tasa_ganadas}%</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>{formatAmount(c.venta_total)}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', color: c.rentabilidad >= 0 ? '#2E7D32' : '#C62828' }}>{formatAmount(c.rentabilidad)}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: c.margen_rentabilidad >= 20 ? '#2E7D32' : c.margen_rentabilidad >= 10 ? '#E65100' : '#C62828' }}>{c.margen_rentabilidad}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
